import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AnimatePresence, motion } from "framer-motion";
import { cn } from "lib/utils";
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Check,
  ChevronRight,
  CreditCard,
  Loader2,
  Lock,
  Store,
  X,
} from "lucide-react";

import { useCancelQuotaOrder, useCreateQuotaOrder } from "hooks/useQuotas";

import PaynetReceipt from "js/components/Payments/PaynetReceipt";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "js/components/ui/alert-dialog";
import SpeiPaymentSlip from "js/components/Payments/SpeiPaymentSlip";
import {
  QUOTE_TYPE_LABELS,
  formatCurrency,
} from "js/components/Quotas/QuotaConstants";
import { Button, buttonVariants } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";

import type { PaymentData } from "js/pages/Payments/types";
import type {
  CreateOrderResponse,
  OrderStatus,
  PaymentMethod,
  QuotaOrder,
  QuotaPackage,
} from "../types";

import CardPaymentForm from "./CardPaymentForm";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

// Cargos con tarjeta por $3,000 o más son rechazados por el procesador,
// así que a partir de ese monto forzamos SPEI / Paynet (efectivo).
const CARD_MAX_AMOUNT = 3_000;
const PAYNET_MAX_AMOUNT = 29_999;

type QuoteType = "PF" | "PM";
type Step = "packages" | "method" | "payment" | "receipt";

const STEPS: Step[] = ["packages", "method", "payment"];

interface PurchaseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteType: QuoteType;
  packages: QuotaPackage[];
}

const METHODS: {
  key: PaymentMethod;
  label: string;
  desc: string;
  icon: typeof CreditCard;
  maxAmount?: number;
}[] = [
  { key: "card", label: "Tarjeta de crédito/débito", desc: "Pago inmediato · Al instante", icon: CreditCard, maxAmount: CARD_MAX_AMOUNT },
  { key: "store", label: "Efectivo (Paynet)", desc: "Más de 25,000 puntos de venta · 1-24 hrs", icon: Store, maxAmount: PAYNET_MAX_AMOUNT },
  { key: "bank_account", label: "Transferencia SPEI", desc: "Sin comisión · Acreditación en minutos", icon: Building2 },
];

const TYPE_LABELS: Record<QuoteType, string> = { PF: "Persona Física", PM: "Persona Moral" };

const anim = {
  initial: { opacity: 0, x: 12 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, x: -12, transition: { duration: 0.12 } },
};

/* ------------------------------------------------------------------ */
/*  Step indicator                                                     */
/* ------------------------------------------------------------------ */

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-1 rounded-full transition-all duration-300",
            i <= current
              ? "w-5 bg-foreground"
              : "w-1.5 bg-muted-foreground/25",
          )}
        />
      ))}
    </div>
  );
}

/* ================================================================== */

function PurchaseSheet({ open, onOpenChange, quoteType, packages }: PurchaseSheetProps) {
  const navigate = useNavigate();
  const createOrder = useCreateQuotaOrder();
  const cancelOrder = useCancelQuotaOrder();

  const [step, setStep] = useState<Step>("packages");
  const [pkg, setPkg] = useState<QuotaPackage | null>(null);
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState<QuotaOrder | null>(null);

  const filtered = useMemo(
    () => packages.filter((p) => p.quote_type === quoteType).sort((a, b) => a.display_order - b.display_order),
    [packages, quoteType],
  );

  const baseUnit = useMemo(
    () => (filtered.length > 0 ? filtered[0].price / filtered[0].quantity : 0),
    [filtered],
  );

  const bestPkg = useMemo(() => {
    if (filtered.length <= 1) return null;
    return filtered.reduce((b, p) => {
      return p.price / p.quantity < b.price / b.quantity ? p : b;
    }, filtered[0]);
  }, [filtered]);

  const unitPrice = (p: QuotaPackage) => p.price / p.quantity;
  const savingsPct = (p: QuotaPackage) => {
    if (baseUnit <= 0) return 0;
    return Math.round((1 - unitPrice(p) / baseUnit) * 100);
  };

  useEffect(() => {
    if (open) {
      setStep("packages");
      setPkg(null);
      setMethod(null);
      setSubmitting(false);
      setOrder(null);
      createOrder.reset();
    }
  }, [open]);

  const price = pkg ? pkg.price : 0;
  const stepIndex = STEPS.indexOf(step);

  /* ── Navigation ──────────────────────────────────────── */

  const goNext = () => {
    if (step === "packages" && pkg) setStep("method");
    else if (step === "method" && method) setStep("payment");
  };

  const goBack = () => {
    if (step === "method") { setMethod(null); setStep("packages"); }
    else if (step === "payment") { createOrder.reset(); setMethod(null); setStep("method"); }
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const hasProgress = step !== "packages";

  const requestClose = () => {
    if (submitting) return;
    if (hasProgress) {
      setConfirmCloseOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  const TERMINAL_STATUSES: OrderStatus[] = ["COMPLETED", "FAILED", "CANCELLED"];

  const confirmClose = async () => {
    setConfirmCloseOpen(false);

    // If there's an active (non-terminal) order, cancel it before closing
    if (order?.id && !TERMINAL_STATUSES.includes(order.status)) {
      try {
        await cancelOrder.mutateAsync(order.id);
      } catch {
        // Order may already be completed/cancelled — close the sheet anyway
      }
    }

    onOpenChange(false);
  };

  /* ── Handlers ────────────────────────────────────────── */

  const onCardSubmit = async (d: { token_id: string; device_session_id: string }) => {
    if (!pkg) return;
    setSubmitting(true);
    try {
      const r: CreateOrderResponse = await createOrder.mutateAsync({
        package_id: pkg.id, method: "card", token_id: d.token_id, device_session_id: d.device_session_id,
      });
      if (r.charge?.redirect_url) { window.location.href = r.charge.redirect_url; return; }
      onOpenChange(false);
      navigate(`/organizations/quotas/orders/${r.order.id}`);
    } catch {
      // Error toast handled by useCreateQuotaOrder hook
    } finally { setSubmitting(false); }
  };

  const onOfflineConfirm = async () => {
    if (!pkg || !method || method === "card") return;
    setSubmitting(true);
    try {
      const r: CreateOrderResponse = await createOrder.mutateAsync({ package_id: pkg.id, method });
      setOrder(r.order);
      setStep("receipt");
    } catch {
      // Error toast handled by useCreateQuotaOrder hook
    } finally { setSubmitting(false); }
  };

  const viewOrder = () => {
    if (order?.id) { onOpenChange(false); navigate(`/organizations/quotas/orders/${order.id}`); }
  };

  const handleChangeMethod = async () => {
    if (!order?.id) return;
    try {
      await cancelOrder.mutateAsync(order.id);
      setOrder(null);
      setMethod(null);
      createOrder.reset();
      setStep("method");
    } catch {
      // Error toast handled by useCancelQuotaOrder hook
    }
  };

  /* ── Header content per step ─────────────────────────── */

  const headerTitle = (() => {
    switch (step) {
      case "packages": return `Recargar ${TYPE_LABELS[quoteType]}`;
      case "method": return "Método de pago";
      case "payment": return method === "card" ? "Datos de tarjeta" : "Confirmar pago";
      case "receipt": return "Orden de pago";
    }
  })();

  const headerDesc = (() => {
    switch (step) {
      case "packages": return "Selecciona un paquete de consultas";
      case "method": return "Elige cómo quieres pagar";
      case "payment": return method === "card" ? "Ingresa los datos de tu tarjeta" : "Revisa los detalles antes de confirmar";
      case "receipt": return "Guarda o descarga tu orden de pago";
    }
  })();

  /* ── Step: Packages ──────────────────────────────────── */

  const renderPackages = () => (
    <div className="flex flex-col gap-2">
      {filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No hay paquetes disponibles
        </p>
      ) : (
        filtered.map((p) => {
          const isSelected = pkg?.id === p.id;
          const isBest = bestPkg?.id === p.id;
          const sv = savingsPct(p);
          const total = p.price;

          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setPkg(p)}
              className={cn(
                "flex w-full items-center gap-4 rounded-xl border px-4 py-3.5 text-left transition-all",
                isBest && !isSelected && "border-amber-200/70 bg-amber-50/30 hover:bg-amber-50/50",
                isBest && isSelected && "border-amber-300/80 bg-amber-50/40 shadow-sm",
                !isBest && isSelected && "border-foreground/70 bg-muted/50 shadow-sm",
                !isBest && !isSelected && "border-border/50 hover:bg-muted/30",
              )}
            >
              {/* Radio indicator */}
              <div
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                  isSelected
                    ? "border-foreground bg-foreground"
                    : "border-muted-foreground/25",
                )}
              >
                {isSelected && <Check className="size-3 text-background" />}
              </div>

              {/* Quantity + badges */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold tabular-nums text-foreground">
                    {p.quantity.toLocaleString("es-MX")} consultas
                  </span>
                  {sv > 0 && (
                    <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-emerald-600">
                      &minus;{sv}%
                    </span>
                  )}
                  {isBest && (
                    <span className="rounded-md bg-amber-100/70 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                      Recomendado
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                  {formatCurrency(unitPrice(p))} por consulta
                </p>
              </div>

              {/* Price */}
              <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                {formatCurrency(total)}
              </span>
            </button>
          );
        })
      )}
    </div>
  );

  /* ── Step: Method ────────────────────────────────────── */

  const renderMethod = () => {
    const cardDisabled = price >= CARD_MAX_AMOUNT;
    // Métodos alternativos que sí admiten este monto (sin la tarjeta).
    const availableAlts = METHODS.filter(
      (m) => m.key !== "card" && (m.maxAmount === undefined || price <= m.maxAmount),
    ).map((m) => m.label);

    return (
    <div className="flex flex-col gap-2">
      {/* Selected package summary */}
      {pkg && (
        <div className="mb-2 flex items-center justify-between rounded-lg bg-muted/30 px-4 py-2.5">
          <span className="text-xs text-muted-foreground">
            {pkg.quantity.toLocaleString("es-MX")} consultas {QUOTE_TYPE_LABELS[pkg.quote_type]}
          </span>
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {formatCurrency(price)}
          </span>
        </div>
      )}

      {/* Aviso: la tarjeta no admite montos altos → usar SPEI / Paynet */}
      {cardDisabled && availableAlts.length > 0 && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200/60 bg-amber-50/40 px-3.5 py-2.5">
          <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
          <p className="text-[11px] leading-relaxed text-amber-700">
            El pago con tarjeta no está disponible para montos de{" "}
            {formatCurrency(CARD_MAX_AMOUNT)} o más. Para este paquete, paga con{" "}
            {availableAlts.join(" o ")}.
          </p>
        </div>
      )}

      {METHODS.map((m) => {
        const disabled =
          m.key === "card"
            ? price >= CARD_MAX_AMOUNT
            : m.maxAmount !== undefined && price > m.maxAmount;
        const isSelected = method === m.key;
        const Icon = m.icon;
        return (
          <button
            key={m.key}
            type="button"
            disabled={disabled}
            onClick={() => setMethod(m.key)}
            className={cn(
              "flex w-full items-center gap-3.5 rounded-xl border-2 px-4 py-3.5 text-left transition-all",
              isSelected
                ? "border-foreground bg-muted/40 shadow-sm"
                : "border-transparent bg-muted/20 hover:bg-muted/40",
              disabled && "cursor-not-allowed opacity-30",
            )}
          >
            {/* Radio indicator */}
            <div
              className={cn(
                "flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                isSelected
                  ? "border-foreground bg-foreground"
                  : "border-muted-foreground/30",
              )}
            >
              {isSelected && <Check className="size-3 text-background" />}
            </div>

            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted/60">
              <Icon className="size-4 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{m.label}</p>
              <p className="text-[11px] text-muted-foreground">
                {disabled ? "No disponible para este monto" : m.desc}
              </p>
            </div>
          </button>
        );
      })}
    </div>
    );
  };

  /* ── Step: Payment ───────────────────────────────────── */

  const renderPayment = () => {
    if (method === "card") {
      return (
        <div className="flex flex-col gap-4">
          <CardPaymentForm
            formId="purchase-card-form"
            onSubmit={onCardSubmit}
            isSubmitting={submitting}
            amount={price}
            hideSubmitButton
          />
        </div>
      );
    }

    const label = method === "bank_account" ? "transferencia SPEI" : "efectivo (Paynet)";

    return (
      <div className="flex flex-col gap-5">
        {/* Order summary */}
        <div className="rounded-xl border border-border/40 divide-y divide-border/40">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-xs text-muted-foreground">Paquete</span>
            <span className="text-sm font-medium text-foreground">
              {pkg?.quantity.toLocaleString("es-MX")} consultas {pkg ? QUOTE_TYPE_LABELS[pkg.quote_type] : ""}
            </span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-xs text-muted-foreground">Método</span>
            <span className="text-sm font-medium capitalize text-foreground">{label}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-xs text-muted-foreground">Total</span>
            <span className="text-base font-semibold tabular-nums text-foreground">
              {formatCurrency(price)}
            </span>
          </div>
        </div>

        <ul className="flex flex-col gap-1.5 text-xs text-muted-foreground">
          <li className="flex items-center gap-2">
            <Check className="size-3 shrink-0 text-emerald-500" />
            Referencia generada automáticamente
          </li>
          <li className="flex items-center gap-2">
            <Check className="size-3 shrink-0 text-emerald-500" />
            Acreditación al recibir el pago
          </li>
        </ul>
      </div>
    );
  };

  /* ── Step: Receipt ───────────────────────────────────── */

  const renderReceipt = () => {
    if (!order) return null;
    const pd = order.payment_data as PaymentData;
    const amt = order.total_amount;

    return (
      <div className="flex flex-col gap-4">
        {method === "bank_account" && pd && <SpeiPaymentSlip paymentData={pd} amount={amt} />}
        {method === "store" && pd && <PaynetReceipt paymentData={pd} amount={amt} />}
      </div>
    );
  };

  /* ── Error ───────────────────────────────────────────── */

  const renderError = () => (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="size-6 text-destructive" />
      </div>
      <p className="text-sm font-medium text-foreground">Error al procesar</p>
      <p className="text-xs text-muted-foreground">
        {(createOrder.error as Error)?.message || "Intenta nuevamente."}
      </p>
    </div>
  );

  /* ── Footer ──────────────────────────────────────────── */

  const renderFooter = () => {
    switch (step) {
      case "packages":
        return (
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={requestClose} className="text-muted-foreground">
              Cancelar
            </Button>
            <Button disabled={!pkg} onClick={goNext} className="gap-1.5">
              Siguiente
              <ChevronRight className="size-4" />
            </Button>
          </div>
        );

      case "method":
        return (
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={goBack} className="gap-1.5 text-muted-foreground">
              <ArrowLeft className="size-3.5" />
              Atrás
            </Button>
            <Button disabled={!method} onClick={goNext} className="gap-1.5">
              Siguiente
              <ChevronRight className="size-4" />
            </Button>
          </div>
        );

      case "payment":
        if (method === "card") {
          return (
            <div className="flex items-center justify-between gap-3">
              <Button variant="ghost" size="sm" onClick={goBack} className="gap-1.5 text-muted-foreground">
                <ArrowLeft className="size-3.5" />
                Atrás
              </Button>
              <Button
                type="submit"
                form="purchase-card-form"
                disabled={submitting}
                className="gap-1.5"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Lock className="size-3.5" />
                    Pagar {formatCurrency(price)}
                  </>
                )}
              </Button>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={goBack} className="gap-1.5 text-muted-foreground">
              <ArrowLeft className="size-3.5" />
              Atrás
            </Button>
            <Button disabled={submitting} onClick={onOfflineConfirm} className="gap-1.5">
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Generando...
                </>
              ) : (
                `Confirmar pago · ${formatCurrency(price)}`
              )}
            </Button>
          </div>
        );

      case "receipt":
        return (
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleChangeMethod}
              disabled={cancelOrder.isPending}
              className="gap-1.5 text-muted-foreground"
            >
              {cancelOrder.isPending ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  Cancelando...
                </>
              ) : (
                <>
                  <ArrowLeft className="size-3.5" />
                  Cambiar método de pago
                </>
              )}
            </Button>
            <Button onClick={viewOrder} className="gap-1.5">
              Ver detalle de orden
              <ChevronRight className="size-4" />
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  /* ── Render ──────────────────────────────────────────── */

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); }}>
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => { e.preventDefault(); requestClose(); }}
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
      >
        {/* ── Header ──────────────────────────────────────── */}
        <DialogHeader className="relative space-y-3 px-6 pt-6 pb-4">
          {step !== "receipt" && (
            <StepIndicator current={stepIndex} total={STEPS.length} />
          )}
          <div className="space-y-1 pr-8">
            <DialogTitle>{headerTitle}</DialogTitle>
            <DialogDescription className="text-xs">
              {headerDesc}
            </DialogDescription>
          </div>
          {!submitting && (
            <button
              type="button"
              onClick={requestClose}
              className="absolute top-5 right-5 rounded-sm opacity-70 transition-opacity hover:opacity-100"
            >
              <X className="size-4" />
              <span className="sr-only">Cerrar</span>
            </button>
          )}
        </DialogHeader>

        {/* ── Body ────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={step} {...anim}>
              {step === "packages" && renderPackages()}
              {step === "method" && renderMethod()}
              {step === "payment" && (createOrder.isError ? renderError() : renderPayment())}
              {step === "receipt" && renderReceipt()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── Footer ──────────────────────────────────────── */}
        <div className="border-t border-border/40 px-6 py-4">
          {method === "card" && step === "payment" && !submitting && (
            <p className="mb-3 flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground/60">
              <Lock className="size-2.5" />
              Pago seguro · VISA · MC · AMEX
            </p>
          )}
          {renderFooter()}
        </div>
      </DialogContent>

      {/* ── Confirm close dialog ──────────────────────────── */}
      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent className="sm:max-w-[340px] p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-sm font-semibold">
              {step === "receipt"
                ? "¿Cancelar orden y salir?"
                : "¿Salir de la compra?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              {step === "receipt"
                ? "Tu orden pendiente será cancelada y tendrás que generar una nueva si deseas continuar."
                : "Tu selección y progreso de pago no se guardarán."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2 sm:flex-row gap-2">
            <AlertDialogCancel className="flex-1 h-9 text-xs">
              Continuar compra
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmClose}
              className={cn(
                buttonVariants({ variant: "destructive" }),
                "flex-1 h-9 text-xs",
              )}
            >
              {step === "receipt" ? "Cancelar orden y salir" : "Salir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

export default PurchaseSheet;
